import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowLeftIcon, XIcon } from "lucide-react";
import { scopeThreadRef } from "@t3tools/client-runtime";

import { Button } from "./ui/button";
import { Sheet, SheetPopup } from "./ui/sheet";
import { SubAgentListView } from "./SubAgentListView";
import { SubAgentThreadView } from "./SubAgentThreadView";
import { taskStatusLabel } from "./chat/taskStatusLabels";
import { selectEnvironmentState, selectSubAgentChildStatus, useStore } from "../store";
import {
  canGoBack,
  currentSubAgentPanelView,
  useSubAgentPanelStore,
  type SubAgentPanelView,
} from "../subAgentPanelStore";

export default function SubAgentPanel() {
  const view = useSubAgentPanelStore(currentSubAgentPanelView);
  const showBack = useSubAgentPanelStore(canGoBack);
  const back = useSubAgentPanelStore((state) => state.back);
  const close = useSubAgentPanelStore((state) => state.close);
  const title = useSubAgentPanelTitle(view);

  return (
    <Sheet
      open={view !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <SheetPopup
        side="right"
        showCloseButton={false}
        className="min-w-80 max-w-none p-0"
        style={{ width: 480 }}
      >
        <div className="flex h-full min-h-0 w-full flex-col">
          <div className="flex h-12 shrink-0 items-center gap-1 border-b border-border/60 px-3">
            {showBack && (
              <Button variant="ghost" size="icon" className="size-7 shrink-0" onClick={back}>
                <ArrowLeftIcon className="size-4" />
              </Button>
            )}
            <div className="min-w-0 flex-1 truncate text-sm font-medium">
              {title ?? <Trans>Agent task</Trans>}
            </div>
            <Button variant="ghost" size="icon" className="size-7 shrink-0" onClick={close}>
              <XIcon className="size-4" />
            </Button>
          </div>
          {view?.kind === "list" && (
            <SubAgentListView environmentId={view.environmentId} threadId={view.threadId} />
          )}
          {view?.kind === "thread" && (
            <SubAgentThreadView
              key={view.threadId}
              environmentId={view.environmentId}
              threadId={view.threadId}
              prompt={view.prompt}
            />
          )}
        </div>
      </SheetPopup>
    </Sheet>
  );
}

function useSubAgentPanelTitle(view: SubAgentPanelView | null): string | null {
  const { i18n, t } = useLingui();
  const threadRef = view ? scopeThreadRef(view.environmentId, view.threadId) : null;
  const threadTitle = useStore((state) =>
    view?.kind === "thread"
      ? selectEnvironmentState(state, view.environmentId).threadShellById[view.threadId]?.title
      : undefined,
  );
  const status = useStore((state) =>
    view?.kind === "thread" ? selectSubAgentChildStatus(state, threadRef) : null,
  );

  if (!view) {
    return null;
  }
  if (view.kind === "list") {
    return t`Agents`;
  }
  const baseTitle = threadTitle ?? view.title;
  return status ? `${baseTitle} · ${taskStatusLabel(i18n, status)}` : baseTitle;
}
